import { useEffect, useMemo, useState } from 'react'
import {
  Control,
  useController,
  useFormContext,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form'
import { RadioButtonSelect } from 'components/FormUtils/RadioButtonSelect'
import { Building2, MapPin } from 'lucide-react'
import { Tabs } from 'components/FbUI/Tabs'
import { SelectLocationsForDealTable } from 'components/Tables/SelectLocationsForDealTable/SelectLocationsForDealTable'
import { z } from 'zod'

export const selectDoorsStepFormSchema = z.object({
  apply_to: z
    .union([z.literal('entire_company'), z.literal('select_locations')])
    .default('entire_company'),
  door_ids: z.array(z.number()).optional(),
  is_all_doors_selected: z.boolean().optional(),
})

interface SelectDoorsStepFormValues {
  apply_to: 'entire_company' | 'select_locations'
  door_ids?: number[]
  is_all_doors_selected?: boolean
}

export const checkDoorSelectionRules = (
  val: SelectDoorsStepFormValues,
  ctx: z.RefinementCtx
) => {
  if (val.apply_to === 'select_locations' && val.is_all_doors_selected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You cannot select all doors, use "Entire Company" instead',
      path: ['door_ids'],
    })
  } else if (
    val.apply_to === 'select_locations' &&
    (!val.door_ids || val.door_ids.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You must select at least one door',
      path: ['door_ids'],
    })
  }
}

interface SelectDoorsStepProps {
  chainProxyId: number
  companyTotalDoors: number
  radioGroupLabel?: string
}

export const SelectDoorsStep = ({
  chainProxyId,
  companyTotalDoors,
  radioGroupLabel,
}: SelectDoorsStepProps) => {
  const { control, trigger: formTrigger } =
    useFormContext<SelectDoorsStepFormValues>()
  return (
    <SelectDoorsStepWithControl
      chainProxyId={chainProxyId}
      companyTotalDoors={companyTotalDoors}
      radioGroupLabel={radioGroupLabel}
      control={control}
      formTrigger={formTrigger}
    />
  )
}

interface SelectDoorsStepWithControlProps<
  T extends SelectDoorsStepFormValues,
> extends SelectDoorsStepProps {
  control: Control<T>
  formTrigger: UseFormTrigger<T>
}

export const SelectDoorsStepWithControl = <
  T extends SelectDoorsStepFormValues,
>({
  control: _control,
  chainProxyId,
  companyTotalDoors,
  formTrigger: _formTrigger,
  radioGroupLabel = 'Apply this deal to:',
}: SelectDoorsStepWithControlProps<T>) => {
  const control = _control as unknown as Control<SelectDoorsStepFormValues>
  const formTrigger =
    _formTrigger as unknown as UseFormTrigger<SelectDoorsStepFormValues>

  const tableKey = `SELECT_LOCATIONS_${chainProxyId}`

  const radioGroupOptions = useMemo(() => {
    return [
      {
        key: 'entire_company',
        icon: Building2,
        label: 'Entire Company',
      },
      {
        key: 'select_locations',
        icon: MapPin,
        label: 'Select locations',
        props: {
          disabled: companyTotalDoors === 1,
        },
        tooltip:
          companyTotalDoors === 1
            ? 'There is only one location for this company'
            : undefined,
      },
    ]
  }, [companyTotalDoors])

  const {
    field: { onChange: onChangeDoorIds, value: selectedIds = [] },
    fieldState: { error: doorIdsError },
  } = useController({ name: 'door_ids', control })

  const {
    field: { onChange: onChangeIsAllDoorsSelected },
  } = useController({ name: 'is_all_doors_selected', control })

  const applyTo = useWatch({ name: 'apply_to', control })

  const isSelectLocationsSelected = applyTo === 'select_locations'

  const [selectedTab, setSelectedTab] = useState<'selected' | 'not_selected'>(
    'selected'
  )
  const [selectedCount, setSelectedCount] = useState(0)
  const [notSelectedCount, setNotSelectedCount] = useState(0)

  useEffect(() => {
    if (!isSelectLocationsSelected) {
      onChangeIsAllDoorsSelected(false)
    }
  }, [isSelectLocationsSelected])

  useEffect(() => {
    if (companyTotalDoors) {
      if (companyTotalDoors > 0 && selectedCount >= companyTotalDoors) {
        onChangeIsAllDoorsSelected(true)
      } else {
        onChangeIsAllDoorsSelected(false)
      }
      formTrigger('door_ids')
    }
  }, [selectedCount, companyTotalDoors])

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-4 border border-gray-200 mb-3">
      <div className="flex items-center">
        <span className="text-gray-800 text-base font-medium mr-6 min-w-fit">
          {radioGroupLabel}
        </span>
        <RadioButtonSelect
          control={control}
          name="apply_to"
          options={radioGroupOptions}
          horizontal
        />
      </div>
      {isSelectLocationsSelected && (
        <>
          {doorIdsError?.message && (
            <div className="text-red-500 mt-2">{doorIdsError.message}</div>
          )}
          <div className="mt-4">
            <Tabs
              tabs={[
                {
                  key: 'selected',
                  title: (
                    <div>
                      <b>Selected ({selectedCount})</b>
                    </div>
                  ),
                },
                {
                  key: 'not_selected',
                  title: (
                    <div>
                      <b>Not Selected ({notSelectedCount})</b>
                    </div>
                  ),
                },
              ]}
              selectedTab={selectedTab}
              setSelectedTab={(value) => {
                setSelectedTab(value)
              }}
              tabStyle={{ width: '180px' }}
            />
            <div className={selectedTab !== 'selected' ? 'hidden' : ''}>
              <SelectLocationsForDealTable
                chainProxyId={chainProxyId}
                tableKey={tableKey}
                filterIdentifierModifier="-selected"
                type="selected"
                setTotalRowsCount={setSelectedCount}
                selectedIds={selectedIds}
                onSelectedIdsChange={onChangeDoorIds}
              />
            </div>
            <div className={selectedTab !== 'not_selected' ? 'hidden' : ''}>
              <SelectLocationsForDealTable
                chainProxyId={chainProxyId}
                tableKey={tableKey}
                filterIdentifierModifier="-unselected"
                type="not_selected"
                setTotalRowsCount={setNotSelectedCount}
                selectedIds={selectedIds}
                onSelectedIdsChange={onChangeDoorIds}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
